import { ref } from 'vue'
import { defineStore } from 'pinia'
import { fetchRateOnce } from '@/api/fx'

type PairKey = `${string}->${string}`
type RateEntry = { rate: number; updatedAt: number }
// 刷新策略：ttl=短缓存；live=依赖轮询；nocache=每次都直拉
type RefreshPolicy = 'ttl' | 'live' | 'nocache'

const useExchangeRateStore = defineStore('exchangeRate', () => {
  const rates = ref<Record<PairKey, RateEntry>>({})
  const ttlMs = ref(30_000)
  const policy = ref<RefreshPolicy>('ttl')

  // 定时器/订阅计数作为私有变量，避免被响应式系统追踪
  let timer: number | null = null
  let subscribers = 0

  // 写一个小工具，统一设置正向/反向汇率，减少重复代码
  function setBothDirections(from: string, to: string, rate: number, t = Date.now()) {
    const a = `${from}->${to}` as PairKey
    const b = `${to}->${from}` as PairKey
    rates.value[a] = { rate, updatedAt: t }
    // 反向汇率复用（B->A = 1/(A->B)），可减少一半请求
    rates.value[b] = { rate: 1 / rate, updatedAt: t }
  }

  // 确保某对货币的汇率为"可用"
  async function ensureRate(from: string, to: string): Promise<number> {
    const key = `${from}->${to}` as PairKey
    const now = Date.now()
    const hit = rates.value[key]

    // nocache 策略——每次都直拉最新，不走缓存
    if (policy.value === 'nocache') {
      const latest = await fetchRateOnce({ from_currency: from, to_currency: to })
      setBothDirections(from, to, latest, now)
      return latest
    }

    // TTL 命中直接返回，兼顾"看起来实时"与请求量
    if (hit && now - hit.updatedAt < ttlMs.value) return hit.rate

    if (hit) {
      // 复杂逻辑（SWR）：过期但有旧值——先返回旧值，后台静默刷新
      void (async () => {
        try {
          const fresh = await fetchRateOnce({ from_currency: from, to_currency: to })
          setBothDirections(from, to, fresh)
        } catch {
          /* 静默失败，不打扰当前展示 */
        }
      })()
      return hit.rate
    }

    // 首次或无缓存：拉一次最新
    const latest = await fetchRateOnce({ from_currency: from, to_currency: to })
    setBothDirections(from, to, latest, now)
    return latest
  }

  async function convert(amount: number, from: string, to: string): Promise<number> {
    if (from === to) return amount
    const rate = await ensureRate(from, to)
    return +(amount * rate).toFixed(6)
  }

  // 集中轮询；多组件可叠加订阅，最后一个取消时停轮询
  function startAutoRefresh(pairs: Array<[string, string]>, intervalMs = 15_000) {
    subscribers++
    if (timer) return
    timer = window.setInterval(async () => {
      // 页面不可见时跳过本轮，省电省流量
      if (typeof document !== 'undefined' && (document as any).hidden) return

      const tasks = pairs.map(async ([from, to]) => {
        try {
          // 强制刷新——先令其过期，ensureRate 会拉新
          const key = `${from}->${to}` as PairKey
          delete rates.value[key]
          await ensureRate(from, to)
        } catch {
          /* 静默容错，避免影响其他对 */
        }
      })
      await Promise.allSettled(tasks)
    }, intervalMs) as unknown as number
  }

  function stopAutoRefresh() {
    subscribers = Math.max(0, subscribers - 1)
    if (subscribers === 0 && timer) {
      clearInterval(timer)
      timer = null
    }
  }

  return {
    rates,
    ttlMs,
    policy,
    ensureRate,
    convert,
    startAutoRefresh,
    stopAutoRefresh,
  }
})

export default useExchangeRateStore
